import { useEffect, useRef, useState } from "react";
import { Loader2, FileText, ExternalLink, ArrowLeft } from "lucide-react";
import { mediaRawUrl } from "@/lib/api";
import { adSequence, aid, isPdfItem } from "@/lib/adMedia";
import AdPreview from "@/components/AdPreview";
import { StreamThumb } from "@/components/video/StreamThumb";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

function Attachment({ horseId, m, i, cover, onOpen }) {
  const src = mediaRawUrl(horseId, aid(m), m.rotation);
  const tag = <span className="absolute top-2 left-2 text-[10px] font-bold bg-stone-900/75 text-white rounded-full px-2 py-0.5">#{i + 1}{m.kind === "paper" ? " · Papers" : cover ? " · Cover photo" : m.kind === "video" ? " · Video" : ""}</span>;
  if (m.kind === "video" && m.source === "stream") return (
    <div data-testid={`preview-media-${i}`} className="relative bg-black rounded-lg overflow-hidden">
      <StreamThumb videoId={m.video_id} name={m.name} inline className="aspect-video" />{tag}
    </div>
  );
  if (m.kind === "video") return (
    <div data-testid={`preview-media-${i}`} className="relative bg-black rounded-lg overflow-hidden">
      <video src={src} controls playsInline preload="metadata" className="w-full max-h-[70vh]" />{tag}
    </div>
  );
  if (m.kind === "paper" && isPdfItem(m)) return (
    <div data-testid={`preview-media-${i}`} className="relative rounded-lg border border-stone-200 bg-white overflow-hidden">
      <iframe title={m.name || "Registration papers"} src={`${src}#view=FitH`} className="w-full h-[70vh] bg-white" />{tag}
      <button type="button" onClick={() => onOpen(m)} className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-semibold text-amber-800 border-t border-stone-200 hover:bg-amber-50 transition-colors duration-150">
        <FileText className="h-4 w-4" />View full document
      </button>
    </div>
  );
  return (
    <button type="button" data-testid={`preview-media-${i}`} onClick={() => onOpen(m)} className="relative block w-full bg-stone-100 rounded-lg overflow-hidden">
      <img src={src} alt={m.name} className="w-full h-auto min-h-[200px] max-h-[80vh] object-contain mx-auto" />{tag}
    </button>
  );
}

export function PreviewAdDialog({ open, onOpenChange, horseId, preview, media, loading, textFirst = false }) {
  const [big, setBig] = useState(null);
  const pushed = useRef(false);
  // Browser/phone Back closes the preview and stays on the Ad Template (no navigation away, no lost edits).
  useEffect(() => {
    if (!open) return undefined;
    window.history.pushState({ ...(window.history.state || {}), adPreview: true }, "");
    pushed.current = true;
    const onPop = () => { pushed.current = false; onOpenChange(false); };
    window.addEventListener("popstate", onPop);
    return () => {
      window.removeEventListener("popstate", onPop);
      if (pushed.current) { pushed.current = false; window.history.back(); }
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const selected = adSequence(media);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="preview-ad-dialog" className="w-screen h-[100dvh] max-w-none sm:w-[95vw] sm:max-w-3xl sm:h-[92vh] rounded-none sm:rounded-2xl p-0 flex flex-col gap-0">
        <div className="px-4 py-3 border-b border-stone-200 pr-12 flex items-start gap-2">
          <button type="button" data-testid="preview-back-button" onClick={() => onOpenChange(false)} aria-label="Back to Ad Template" className="h-10 w-10 -ml-2 shrink-0 inline-flex items-center justify-center rounded-full text-stone-700 hover:bg-stone-100"><ArrowLeft className="h-5 w-5" /></button>
          <div>
          <DialogTitle className="font-display text-lg font-bold text-stone-900">Ad Preview</DialogTitle>
          <DialogDescription className="text-xs text-stone-500">How your ad will look, using your latest saved information. Read-only — tap Back to return to the Ad Template.</DialogDescription>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto bg-[#FAF8F5] px-3 sm:px-6 py-4 space-y-4">
          {loading ? <div data-testid="preview-ad-loading" className="flex flex-col items-center gap-2 py-10 text-xs text-stone-500"><Loader2 className="h-6 w-6 animate-spin text-amber-800" />Updating preview with your latest changes…</div> : (
            <>
              {/* Admin Pipeline preview: tabs + ad text first, then photos/videos (seller preview keeps media first). */}
              {textFirst && <AdPreview preview={preview} showVersions={!!preview?.versions} stickyBar />}
              {selected.length > 0 ? (
                <div data-testid="preview-ad-media" className="mx-auto max-w-[600px] space-y-3">
                  {selected.map((m, i) => <Attachment key={aid(m)} horseId={horseId} m={m} i={i} cover={m === selected.find((x) => x.kind === "image")} onOpen={setBig} />)}
                </div>
              ) : <p data-testid="preview-ad-no-media" className="mx-auto max-w-[600px] text-[12px] text-amber-900 bg-amber-50 border border-amber-200 rounded px-2.5 py-1.5">⚠ No attachments selected for the ad yet.</p>}
              {!textFirst && <AdPreview preview={preview} showVersions={!!preview?.versions} />}
            </>
          )}
        </div>
        <Dialog open={!!big} onOpenChange={(o) => !o && setBig(null)}>
          <DialogContent data-testid="preview-attachment-lightbox" className="max-w-5xl w-[96vw] p-2 sm:p-3">
            <DialogTitle className="text-sm px-1 truncate">{big?.name || "Attachment"}</DialogTitle>
            <DialogDescription className="sr-only">Larger view</DialogDescription>
            {big && (isPdfItem(big)
              ? <iframe title="Document" src={mediaRawUrl(horseId, aid(big))} className="w-full h-[78vh] rounded-lg border border-stone-200" />
              : <img src={mediaRawUrl(horseId, aid(big), big.rotation)} alt={big.name} className="w-full max-h-[85vh] object-contain rounded-lg" />)}
            {big && <a data-testid="preview-attachment-open-new" href={mediaRawUrl(horseId, aid(big), big.rotation)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-amber-800 underline px-1"><ExternalLink className="h-3 w-3" />Open in new tab</a>}
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}
